import React, { useState, useContext } from 'react';
import PropTypes from 'prop-types';
import { CircleDashed, CircleCheckBig, FileDown } from 'lucide-react';
import { toast } from 'sonner';
import { useMutation, useQueryClient, useIsMutating } from '@tanstack/react-query';
import Modal from 'components/shared/Modal';
import { PreferencesContext } from 'context';
import { useGetAgency, useAPI } from 'hooks';
import { createDownloadable } from 'helpers';
import Locales from 'locales';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from 'components/ui/card';
import { Button } from 'components/ui/button';
import AuthorizationAlert from './AuthorizationAlert';

const LicenseBlock = ({ currentAgency = null }) => {
  const { api } = useAPI();
  const { minPilgrimForAuthorization } = useContext(PreferencesContext);
  const [openLicenseModal, setOpenLicenseModal] = useState(false);
  const [openCancelLicenseModal, setOpenCancelLicenseModal] = useState(false);
  const queryClient = useQueryClient();
  const isMutating = useIsMutating() > 0;

  const {
    mergerRequested,
    licenceRequested,
    licenceApproved,
    mergerApproved,
    agencyName,
    agencyId,
    agencyBadge,
    registeredPilgrimCount,
    eligiblePilgrimCount,
  } = useGetAgency(currentAgency);

  const hasBadge = agencyBadge !== null;

  const registeredPilgrimsOk = registeredPilgrimCount >= minPilgrimForAuthorization;

  const eligiblePilgrimsOk = eligiblePilgrimCount >= minPilgrimForAuthorization;

  // Determine if the agency is authorized based on the number of validated pilgrims and the presence of an agency badge
  const agencyIsAuthorize = hasBadge && registeredPilgrimsOk && eligiblePilgrimsOk;

  const { mutate: licenseRequest } = useMutation({
    mutationFn: () => api.post(`agency/current/license_request/`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agency/'] });
      queryClient.invalidateQueries({ queryKey: ['agency/stats/default/'] });
      queryClient.invalidateQueries({ queryKey: ['agency/', 'is_sa'] });
      toast.success(
        Locales.formatString(Locales.authorization.toast.licence_request, {
          name: agencyName,
        })
      );
    },
  });

  const { mutate: cancelLicenceRequest } = useMutation({
    mutationFn: () => api.post(`agency/${agencyId}/license_revoke/`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agency/'] });
      queryClient.invalidateQueries({ queryKey: ['agency/stats/default/'] });
      queryClient.invalidateQueries({ queryKey: ['agency/', 'is_sa'] });
      toast.success(
        Locales.formatString(Locales.authorization.toast.revoke_licence_request, {
          name: agencyName,
        })
      );
    },
  });

  const { mutate: downloadAutorization } = useMutation({
    mutationFn: () => api.get(`agency/${agencyId}/generate_certificate/`, { responseType: 'blob' }),
    onSuccess: ({ data }) => {
      createDownloadable(data, `Certificat d'autorisation de la Société ${agencyName}`, 'application/pdf');
      toast.success('Téléchargement réussi');
    },
  });

  const handleOpenLicenseRequestModal = () => setOpenLicenseModal(true);
  const handleOpenCancelLicenseRequestModal = () => setOpenCancelLicenseModal(true);

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          <span className="text-xl">{Locales.authorization.authorization_status}</span>
        </CardTitle>

        {(mergerRequested || licenceApproved) && (
          <CardDescription>
            {mergerRequested && !licenceApproved && (
              <span className="text-gray-600">
                {mergerApproved
                  ? Locales.authorization.alert.merger_approved
                  : Locales.authorization.alert.merger_pending}
              </span>
            )}

            {licenceApproved && <span className="text-gray-600">{Locales.authorization.alert.success_message}</span>}
          </CardDescription>
        )}
      </CardHeader>
      {!mergerRequested && !licenceApproved && (
        <CardContent>
          {!mergerRequested && (
            <>
              <p>{Locales.authorization.conditions_admissibilite}</p>

              <div className="ml-6 mt-2 space-y-1">
                <div className="flex items-center gap-1">
                  {hasBadge ? (
                    <CircleCheckBig className="size-[16px] text-hajj-primary" />
                  ) : (
                    <CircleDashed className="size-[14px]" />
                  )}

                  <span>{Locales.authorization.conditions.badge_identification}</span>
                </div>
                <div className="flex items-center gap-1">
                  {registeredPilgrimsOk ? (
                    <CircleCheckBig className="size-[16px] text-hajj-primary" />
                  ) : (
                    <CircleDashed className="size-[14px]" />
                  )}
                  <span>
                    {Locales.formatString(Locales.authorization.conditions.enregistrement_pelerins, {
                      minActivePilgrim: minPilgrimForAuthorization,
                    })}
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  {eligiblePilgrimsOk ? (
                    <CircleCheckBig className="size-[16px] text-hajj-primary" />
                  ) : (
                    <CircleDashed className="size-[14px]" />
                  )}
                  <span>
                    {Locales.formatString(Locales.authorization.conditions.validate_documents, {
                      minActivePilgrim: minPilgrimForAuthorization,
                    })}
                  </span>
                </div>
              </div>

              <AuthorizationAlert
                condition={agencyIsAuthorize && !licenceRequested && !licenceApproved}
                level="success"
                message={Locales.authorization.alert.success_can_request_authorization}
              />

              <AuthorizationAlert
                condition={!agencyIsAuthorize && !licenceRequested}
                level="warning"
                message={Locales.authorization.alert.warning_message}
              />
            </>
          )}

          {licenceRequested && !licenceApproved && (
            <>
              <AuthorizationAlert
                condition={!mergerRequested}
                level="warning"
                message={Locales.authorization.alert.authorization_request_pending}
              />

              {!mergerRequested && (
                <Button
                  type="button"
                  variant="destructive"
                  className="w-full"
                  disabled={!agencyIsAuthorize || isMutating}
                  onClick={() => handleOpenCancelLicenseRequestModal()}
                >
                  {Locales.authorization.cancel_authorization_request}
                </Button>
              )}
            </>
          )}

          <AuthorizationAlert
            condition={!mergerRequested && licenceApproved}
            level="success"
            message={Locales.authorization.alert.success_message}
          />

          {!mergerRequested && !licenceRequested && (
            <Button
              type="button"
              variant="hajj"
              className="w-full"
              disabled={!agencyIsAuthorize || isMutating}
              onClick={() => handleOpenLicenseRequestModal()}
            >
              {Locales.authorization.authorization_request}
            </Button>
          )}

          {/* request license modal */}
          <Modal
            isOpen={openLicenseModal}
            onOpenChange={setOpenLicenseModal}
            title={Locales.authorization.request_authorization}
            footer={
              <>
                <Button variant="outline" onClick={() => setOpenLicenseModal(false)}>
                  {Locales.base.button.discard}
                </Button>
                <Button
                  variant="hajj"
                  disabled={isMutating}
                  onClick={() => {
                    licenseRequest();
                    setOpenLicenseModal(false);
                  }}
                >
                  {Locales.authorization.request_the_authorization}
                </Button>
              </>
            }
          >
            <div className="space-y-3">
              <p>
                {Locales.formatString(Locales.authorization.request_authorization_message, {
                  name: <strong>{agencyName}</strong>,
                })}
              </p>
              <p>{Locales.authorization.after_giving_autorization}</p>
            </div>
          </Modal>

          {/* cancel license request modal */}
          <Modal
            isOpen={openCancelLicenseModal}
            onOpenChange={setOpenCancelLicenseModal}
            title={Locales.authorization.cancel_request_authorization}
            footer={
              <>
                <Button variant="outline" onClick={() => setOpenCancelLicenseModal(false)}>
                  {Locales.base.button.discard}
                </Button>
                <Button
                  variant="destructive"
                  disabled={isMutating}
                  onClick={() => {
                    cancelLicenceRequest();
                    setOpenCancelLicenseModal(false);
                  }}
                >
                  {Locales.authorization.cancel_request_authorization}
                </Button>
              </>
            }
          >
            <p>
              {Locales.formatString(Locales.authorization.cancel_request_authorization_message, {
                name: <strong>{agencyName}</strong>,
              })}
            </p>
          </Modal>
        </CardContent>
      )}

      {licenceApproved && (
        <CardFooter>
          <Button type="button" variant="hajj" className="w-full" onClick={() => downloadAutorization()}>
            <FileDown />
            Télécharger votre certificat d&apos;autorisation
          </Button>
        </CardFooter>
      )}
    </Card>
  );
};

LicenseBlock.propTypes = {
  currentAgency: PropTypes.shape({}),
  agencyIsAuthorize: PropTypes.bool,
  minPilgrimForAuthorization: PropTypes.number,
};

export default LicenseBlock;
